import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  removeJob,
  markJob,
  getPostings,
  getJobDetails,
  initializeJobData,
  getCompanies,
  getCompany,
} from '../../data/jobs/jobActions';
import {
  createCanGetMore,
  createIsLoading,
  createDidError,
} from '../../data/requests/requestsSelectors';
import {postingsSelector} from '../../data/jobs/jobSelectors';
import { JobListingLayout } from '../static/Layouts';
import Header from '../static/Header';
import JobList from './JobList';
import JobDetail from './JobDetail';
import JobSearch from './JobSearch';
import Filters from '../Filters';
import Modal from '../static/Modal';
import PromotionModal from './PromotionModal';

import createLogger from '../../utils/createLogger';
import {Loading} from "../static/Indicators";

const DEBUG = true;
const debug = createLogger(DEBUG, `JobPosts.js`);


class JobPostings extends React.Component {

  static dependencies = [
    `GET_JOB_POSTINGS_PAGE`,
    `GET_JOB_FILTERS`,
    `PUT_JOB`,
  ];

  static propTypes = {
    get: PropTypes.func.isRequired,
    jobs: PropTypes.array.isRequired,
    didError: PropTypes.bool.isRequired,
    removeJob: PropTypes.func.isRequired,
    canGetMore: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    getJobDetails: PropTypes.func.isRequired,
  };

  /* ------------ lifecycle methods ------------ */
  constructor(props) {
    super(props);
    debug.enter(`constructor`);

    this.state = {
      selected: null,
      toRemove: null,
      modalIsOpen: false,
      matchUrl: '',
      matchStrategy: '',
    }
  }

  componentDidMount() {
    const {
      isAuthenticated,
      initializeJobData,
      isLoading,
      getCompanies,
    } = this.props;
    debug.enter(`componentDidMount`);

    if (isAuthenticated) {
      debug.log(`  calling initializeJobData`);
      initializeJobData();
      getCompanies();
    }
  };
  /*------------ selection utils -------------*/

  select = (id) => this.setState({selected: id});
  deselect = () => this.setState({selected: null});

  onSelect = (id) => () => {
    const { select, deselect } = this;
    const { getJobDetails } = this.props;
    const { selected } = this.state;

    if (id !== selected) {
      getJobDetails(id);
      select(id);
    } else {
      deselect();
    }
  };

  selectNext = () => {
    const {jobs} = this.props;
    const {findJob, onSelect, deselect} = this;
    let i = jobs.findIndex(findJob);
    debug.log('SELECT NEXT:', jobs.length);
    if (jobs.length === 1) {
      deselect();
      return
    }
    if (i+1 === jobs.length){
      onSelect(jobs[jobs.length-2]['id'])();
    } else {
      onSelect(jobs[i+1]['id'])();
    }
  };

  findJob = (job) => {
    const {selected} = this.state;
    if (job['id'] === selected) {
      return job;
    }
  };

  /* ------------ event handlers ------------ */

  selectMatch = (url, strategy) => {
    this.setState({matchUrl: url, matchStrategy: strategy});
    console.log('Selected match')
  };

  remove = () => {
    const {selected} = this.state;
    const {jobs, removeJob} = this.props;
    const {selectNext, deselect} = this;
    if (!selected) {
      return;
    }
    if (jobs.length > 1){
      selectNext();
    } else {
      deselect();
    }
    debug.log('removing job...');
    removeJob(selected, 'closed');
  };

  markAsFailed = () => {
    const {selected} = this.state;
    const {jobs, markJob} = this.props;
    const {selectNext, deselect} = this;
    if (!selected) {
      return;
    }
    if (jobs.length > 1){
      selectNext();
    } else {
      deselect();
    }
    markJob(selected, 'failed');
  };

  cancelRemove = () => {
    this.setState({ toRemove: null });
  };

  openModal = () => {
    this.setState({modalIsOpen: true});
    console.log('setting modal to Open #######');
    console.log(this.state);
  };

  closeModal = () => {
    this.setState({modalIsOpen: false})
  };

  /* ------------ render methods ------------ */

  render() {

    const {
      remove,
      deselect,
      onSelect,
      openModal,
      closeModal,
      selectNext,
      selectMatch,
      markAsFailed,
    } = this;

    const {
      canGetMore,
      isLoading,
      didError,
      jobs,
      get,
      companies,
      getCompany,
      getCompanies,
    } = this.props;

    const {
      selected,
      modalIsOpen,
      matchUrl,
      matchStrategy,
    } = this.state;

    debug.enter(`render`);
    debug.log(`  isLoading: ${isLoading}`);
    debug.log(`  didError: ${didError}`);
    debug.log(`  jobs: ${jobs.length}`);

    const listConfig = {
      type: 'postings',
      get,
      jobs,
      title: `Job Postings`,
      didError,
      isLoading,
      canGetMore,
      minimized: !!selected,
      selected,
      onSelect,
      onRemove: remove,
      deselect,
      owp: false,
    };

    return (
      <JobListingLayout>

        <PromotionModal selected={selected} matchUrl={matchUrl} companies={companies} getCompany={getCompany}
                        selectNext={selectNext} matchStrategy={matchStrategy}
                        openModal={openModal}
                        closeModal={closeModal}
                        modalIsOpen={modalIsOpen}/>

        <Header top/>

        { !selected
            ? <Filters onChange={deselect}/>
            : null
        }

        <JobList {...listConfig}/>

        { selected
            ? <JobDetail id={selected}
                         close={deselect}
                         onRemove={remove}
                         markStrategyFailure={markAsFailed}/>
            : null
        }

        { selected
            ? <JobSearch id={selected} openModal={openModal} selectMatch={selectMatch}/>
            : null
        }

      </JobListingLayout>
    );
  }
}

/* ------------ redux connect ------------ */
const mapStateToProps = (state) => {
  const canGetMore = createCanGetMore(JobPostings.dependencies[0]);
  const isLoading = createIsLoading(JobPostings.dependencies);
  const didError = createDidError(JobPostings.dependencies);

  return {
    isAuthenticated: !!state.auth.session,
    jobs: postingsSelector(state),
    companies: state.jobs.companies,
    canGetMore: canGetMore(state),
    isLoading: isLoading(state),
    didError: didError(state),
  }
};

export default connect(mapStateToProps, {
  get: getPostings,
  getCompanies,
  getCompany,
  getJobDetails,
  removeJob,
  markJob,
  initializeJobData,
})(JobPostings);



// WEBPACK FOOTER //
// ./src/components/AgencyPostings/JobPostings.js